import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCancelOrder } from "../../hooks/useOrders";
import type { Order } from "../../services/ordersApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { normalizeError } from "@/shared/errors/normalizeError";

interface CancelOrderDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialReason?: string;
}

export const CancelOrderDialog = ({
  order,
  open,
  onOpenChange,
  initialReason,
}: CancelOrderDialogProps) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const cancelMutation = useCancelOrder();

  useEffect(() => {
    if (open && initialReason) {
      setReason(initialReason);
    }
  }, [initialReason, open]);

  const handleSubmit = () => {
    if (!reason) {
      toast.error("Please provide a cancellation reason");
      return;
    }

    setConflictMessage(null);
    cancelMutation.mutate(
      {
        orderId: order.id,
        reason,
        notes,
      },
      {
        onSuccess: () => {
          setReason("");
          setNotes("");
          onOpenChange(false);
          toast.success("Order cancelled successfully");
        },
        onError: (error) => {
          const envelope = normalizeError(error);
          const message = [envelope.userMessage, envelope.nextStep]
            .filter(Boolean)
            .join(" ");
          setConflictMessage(message || "The order could not be cancelled. Reload it and review its operational commitments.");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Order</DialogTitle>
          <DialogDescription>
            This action will cancel the order and release stock reservations
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Cancellation rules apply based on delivery date. You may incur
              penalties.
            </AlertDescription>
          </Alert>

          {conflictMessage && (
            <Alert variant="destructive" role="alert">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{conflictMessage}</AlertDescription>
            </Alert>
          )}

          <Field>
            <FieldLabel>Reason *</FieldLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this order being cancelled?"
              disabled={cancelMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes (optional)"
              disabled={cancelMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={cancelMutation.isPending}
            >
              Keep Order
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Cancelling..." : "Cancel Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
